import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Phone, Mail, Menu, X, ShieldCheck, Globe } from "lucide-react";
import { services } from "../data/services";
import { provinces } from "../data/provinces";
import { cities } from "../data/cities";
import { PHONE, PHONE_TEL, EMAIL, EMAIL_MAILTO } from "../seo/routes";

const navEn = [
  { to: "/services", label: "Services", testid: "nav-services-link" },
  { to: "/sectors", label: "Sectors", testid: "nav-sectors-link" },
  { to: "/service-areas", label: "Service Areas", testid: "nav-areas-link" },
  { to: "/regions", label: "Regions", testid: "nav-regions-link" },
  { to: "/insights", label: "Insights", testid: "nav-insights-link" },
  { to: "/careers", label: "Careers", testid: "nav-careers-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

const navFr = [
  { to: "/fr", label: "Accueil", testid: "nav-fr-home-link" },
  { to: "/fr/services", label: "Services", testid: "nav-fr-services-link" },
  { to: "/fr/villes", label: "Villes", testid: "nav-fr-villes-link" },
  { to: "/careers", label: "Carrières", testid: "nav-fr-careers-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isFr = pathname === "/fr" || pathname.startsWith("/fr/");
  const navItems = isFr ? navFr : navEn;
  const homeTo = isFr ? "/fr" : "/";
  return (
    <header className="sticky top-0 z-40 bg-[#0D0E12]/90 backdrop-blur-xl border-b border-white/10" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to={homeTo} className="flex items-center gap-3 shrink-0" data-testid="header-logo-link">
          <img src="/favicon.png" alt="Maze Security Solutions logo" className="h-9 w-9 rounded-sm" width="36" height="36" />
          <span className="font-display text-xl font-bold tracking-wide uppercase text-white leading-none">
            Maze <span className="text-[#FF5500]">Security</span>
            <span className="block text-[10px] font-body font-normal tracking-[0.3em] text-gray-400">Canada Security Guards</span>
          </span>
        </Link>
        <nav className="hidden lg:flex items-center gap-4" data-testid="main-navigation">
          {navItems.map((n) => (
            <NavLink
              key={n.to + n.label}
              to={n.to}
              end={n.to === "/fr" || n.to === "/"}
              data-testid={n.testid}
              className={({ isActive }) =>
                `text-[13px] font-medium uppercase tracking-wider transition-colors duration-200 ${
                  isActive ? "text-[#FF5500]" : "text-gray-300 hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to={isFr ? "/" : "/fr"}
            data-testid="language-toggle"
            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-gray-400 hover:text-[#FF5500] border border-white/15 hover:border-[#FF5500]/50 px-2.5 py-1.5 rounded-sm transition-colors"
          >
            <Globe className="h-3.5 w-3.5" /> {isFr ? "EN" : "FR"}
          </Link>
          <a
            href={PHONE_TEL}
            data-testid="header-phone-button"
            className="hidden md:inline-flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] text-white text-sm font-semibold px-4 py-2 rounded-sm transition-colors duration-200"
          >
            <Phone className="h-4 w-4" /> {PHONE}
          </a>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-gray-200 hover:text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle navigation menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-white/10 bg-[#0D0E12] px-4 py-4 flex flex-col gap-3" data-testid="mobile-navigation">
          {navItems.map((n) => (
            <Link
              key={n.to + n.label}
              to={n.to}
              data-testid={`mobile-${n.testid}`}
              onClick={() => setOpen(false)}
              className="text-sm font-medium uppercase tracking-wider text-gray-300 hover:text-[#FF5500] py-1"
            >
              {n.label}
            </Link>
          ))}
          {!isFr && (
            <Link to="/about" data-testid="mobile-nav-about-link" onClick={() => setOpen(false)} className="text-sm font-medium uppercase tracking-wider text-gray-300 hover:text-[#FF5500] py-1">
              About Us
            </Link>
          )}
          <a href={PHONE_TEL} data-testid="mobile-phone-button" className="inline-flex items-center gap-2 text-[#FF5500] font-semibold text-sm pt-2">
            <Phone className="h-4 w-4" /> {PHONE}
          </a>
        </nav>
      )}
    </header>
  );
};

const StickyBar = () => (
  <div
    className="fixed bottom-0 left-0 right-0 z-50 bg-[#131620]/95 backdrop-blur-md border-t border-[#FF5500]/30 shadow-[0_-8px_30px_rgba(0,0,0,0.8)]"
    data-testid="sticky-contact-bar"
  >
    <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
      <div className="hidden sm:flex items-center gap-2" data-testid="dispatch-status-badge">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
        </span>
        <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-400">24/7 Dispatch Active</span>
      </div>
      <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto justify-between sm:justify-end">
        <a
          href={PHONE_TEL}
          data-testid="sticky-phone-button"
          className="inline-flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] text-white text-xs sm:text-sm font-semibold px-3 sm:px-5 py-2 rounded-sm transition-colors duration-200"
        >
          <Phone className="h-4 w-4 shrink-0" /> {PHONE}
        </a>
        <a
          href={EMAIL_MAILTO}
          data-testid="sticky-email-button"
          className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 text-gray-100 text-xs sm:text-sm font-medium px-3 sm:px-5 py-2 rounded-sm transition-colors duration-200"
        >
          <Mail className="h-4 w-4 shrink-0 text-[#FF5500]" /> {EMAIL}
        </a>
      </div>
    </div>
  </div>
);

const Footer = () => (
  <footer className="bg-[#0A0B0F] border-t border-white/10 pb-28" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10">
      <div className="col-span-2 md:col-span-1">
        <div className="flex items-center gap-2 mb-4">
          <img src="/favicon.png" alt="Maze Security Solutions M logo" className="h-8 w-8" width="32" height="32" />
          <span className="font-display text-lg font-bold uppercase text-white">Maze <span className="text-[#FF5500]">Security</span></span>
        </div>
        <p className="text-sm text-gray-400 leading-relaxed">
          Licensed security guard services across all 13 Canadian provinces and territories. Verified patrols, 24/7 dispatch, accountable reporting.
        </p>
        <div className="mt-4 space-y-2">
          <a href={PHONE_TEL} data-testid="footer-phone-link" className="flex items-center gap-2 text-sm text-gray-300 hover:text-[#FF5500] transition-colors">
            <Phone className="h-4 w-4 text-[#FF5500]" /> {PHONE}
          </a>
          <a href={EMAIL_MAILTO} data-testid="footer-email-link" className="flex items-center gap-2 text-sm text-gray-300 hover:text-[#FF5500] transition-colors">
            <Mail className="h-4 w-4 text-[#FF5500]" /> {EMAIL}
          </a>
        </div>
      </div>
      <div>
        <h3 className="text-xs font-mono uppercase tracking-[0.25em] text-[#FF5500] mb-4">Services</h3>
        <ul className="space-y-2">
          {services.slice(0, 10).map((s) => (
            <li key={s.slug}>
              <Link to={`/services/${s.slug}`} data-testid={`footer-service-${s.slug}`} className="text-sm text-gray-400 hover:text-white transition-colors">
                {s.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h3 className="text-xs font-mono uppercase tracking-[0.25em] text-[#FF5500] mb-4">More Services</h3>
        <ul className="space-y-2">
          {services.slice(10).map((s) => (
            <li key={s.slug}>
              <Link to={`/services/${s.slug}`} data-testid={`footer-service-${s.slug}`} className="text-sm text-gray-400 hover:text-white transition-colors">
                {s.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h3 className="text-xs font-mono uppercase tracking-[0.25em] text-[#FF5500] mb-4">Service Areas</h3>
        <ul className="space-y-2">
          {provinces.slice(0, 4).map((p) => (
            <li key={p.slug}>
              <Link to={`/security-guards-${p.slug}`} data-testid={`footer-province-${p.slug}`} className="text-sm text-gray-400 hover:text-white transition-colors">
                {p.name}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/regions" data-testid="footer-regions-link" className="text-sm text-gray-400 hover:text-white transition-colors">
              Regions (GTA, Metro Vancouver…)
            </Link>
          </li>
          <li>
            <Link to="/service-areas" data-testid="footer-all-areas-link" className="text-sm text-[#FF5500] hover:text-white transition-colors">
              All 690+ cities →
            </Link>
          </li>
        </ul>
      </div>
      <div>
        <h3 className="text-xs font-mono uppercase tracking-[0.25em] text-[#FF5500] mb-4">Company</h3>
        <ul className="space-y-2">
          <li><Link to="/about" data-testid="footer-about-link" className="text-sm text-gray-400 hover:text-white transition-colors">About Us</Link></li>
          <li><Link to="/careers" data-testid="footer-careers-link" className="text-sm text-gray-400 hover:text-white transition-colors">Careers</Link></li>
          <li><Link to="/contact" data-testid="footer-contact-link" className="text-sm text-gray-400 hover:text-white transition-colors">Contact</Link></li>
          <li><Link to="/insights" data-testid="footer-insights-link" className="text-sm text-gray-400 hover:text-white transition-colors">Insights</Link></li>
        </ul>
        <h3 className="text-xs font-mono uppercase tracking-[0.25em] text-[#FF5500] mt-6 mb-3">Français</h3>
        <ul className="space-y-2">
          <li><Link to="/fr" data-testid="footer-fr-home" className="text-sm text-gray-400 hover:text-white transition-colors">Accueil (FR)</Link></li>
          <li><Link to="/fr/services" data-testid="footer-fr-services" className="text-sm text-gray-400 hover:text-white transition-colors">Services (FR)</Link></li>
          <li><Link to="/fr/villes" data-testid="footer-fr-villes" className="text-sm text-gray-400 hover:text-white transition-colors">Villes du Québec</Link></li>
        </ul>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3">
      <p className="text-xs text-gray-500">© 2026 Maze Security Solutions — Canada Security Guards. All rights reserved.</p>
      <p className="text-xs text-gray-500">
        Part of the Maze Security Solutions network —{" "}
        <a href="https://mazesecurity.ca" data-testid="footer-partner-link" className="hover:text-[#FF5500] transition-colors" rel="noopener">
          mazesecurity.ca
        </a>
      </p>
      <p className="text-xs text-gray-500 flex items-center gap-1.5">
        <ShieldCheck className="h-3.5 w-3.5 text-[#FF5500]" /> Licensed security services in every Canadian province & territory
      </p>
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
      <p className="text-[11px] leading-relaxed text-gray-600">
        Popular searches: {cities.slice(0, 12).map((c, i) => (
          <span key={c.slug}>
            {i > 0 && " · "}
            <Link to={`/security-guards-${c.slug}`} data-testid={`footer-city-${c.slug}`} className="hover:text-gray-400 transition-colors">
              security guards {c.name}
            </Link>
          </span>
        ))}
      </p>
    </div>
  </footer>
);

export const Layout = () => (
  <div className="min-h-screen bg-[#0D0E12] text-gray-100 flex flex-col">
    <Header />
    <main className="flex-1" data-testid="main-content">
      <Outlet />
    </main>
    <Footer />
    <StickyBar />
  </div>
);
